import AssetLoadService from "../../../../../../../shared/src/ranj/app/assetloading/AssetLoadService";
import AssetLoadServiceEvent from "../../../../../../../shared/src/ranj/app/assetloading/AssetLoadServiceEvent";
import AbstractCommand from "../../../../../../../shared/src/ranj/app/core/AbstractCommand";
import SkinKeys from "../../../../../../../shared/src/ranj/app/ui/SkinKeys";
import PlatformAPIService from "../../../../../../../shared/src/ranj/equalture/platform/PlatformAPIService";
import ProgressValue from "../../../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import SaveEvent from "../../../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import GameModel from "../../../core/model/GameModel";
import TrackingController from "../../../tracking/controller/TrackingController";
import NGTVariableManager from "../../base/NGTVariableManager";
import NGTScenarioPlayer from "../NGTScenarioPlayer";
import ScenarioLibrary from "../ScenarioLibrary";
import ScenarioPlayerEvent from "../ScenarioPlayerEvent";
import ScenarioPlayerRegisterEvent from "./ScenarioPlayerRegisterEvent";
import Config from "../../../../../../../shared/src/ranj/app/config/Config";
import SaveModel from "../../../core/model/SaveModel";
import { getURLParameter } from "../../../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import ScenarioCommandEvent from "./ScenarioCommandEvent";

/**
* PlayScenarioCommand.js
* 
* Created on 2-okt-2013, 16:08:55
*
* @author Ivo Swartjes for Ranj
**/
export default class PlayScenarioCommand extends AbstractCommand {

    /**
     * 
     * @param {*} storyFactory 
     * @param {*} playSessionModel 
     * @param {ScenarioLibrary} scenarioLibrary 
     * @param {NGTScenarioPlayer} scenarioPlayer 
     * @param {*} dispatcher 
     * @param {AssetLoadService} assetLoadService 
     * @param {*} assetLocator 
     * @param {NGTVariableManager} variableManager 
     * @param {TrackingController} trackingController 
     * @param {GameModel} gameModel 
     * @param {PlatformAPIService} platformAPIService
     * @param {Config} config;
     * @param {SaveModel} saveModel
     */
    constructor(storyFactory, playSessionModel, scenarioLibrary, scenarioPlayer,
        dispatcher, assetLoadService, assetLocator, variableManager, trackingController, gameModel,
        platformAPIService, config, saveModel) {

        super(dispatcher);
        this._scenarioToPlay;
        this._storyFactory = storyFactory;
        this._playSessionModel = playSessionModel;
        this._scenarioLibrary = scenarioLibrary;
        this._scenarioPlayer = scenarioPlayer;
        this._assetLocator = assetLocator;
        this._assetLoadService = assetLoadService;
        this._variableManager = variableManager;
        this._trackingController = trackingController;
        this._gameModel = gameModel;
        this._platformAPIService = platformAPIService;
        this._config = config;
        this._saveModel = saveModel;

        this._onScenarioLoadedBound = this.onScenarioLoaded.bind(this);
        this._onScenarioEndBound = this.onScenarioEnd.bind(this);
        this._handleTickBound = this._handleTick.bind(this)
    }

    async execute(event) {
        // reset playthrough in GameModel
        this._gameModel.startPlaythrough();

        // set scenario
        this._scenarioId = event.params.scenarioId;
        this._scenarioToPlay = this._scenarioLibrary.getScenarioByID(this._scenarioId);

        // get configuration from Platform for this scenario
        const appId = this._config.getValue(Config.APPID);
        const fullAppId = `${appId}.${this._scenarioId}`;
        const platformParameters = await this._platformAPIService.getConfigurationAsync(fullAppId);

        // load save game - if any
        const saveGameObj = getURLParameter("clear") === "1" ? null : platformParameters.state;
        if (!saveGameObj) {
            this._saveModel.resetSaveGame();
        } else {
            this._saveModel.setSaveGame(saveGameObj);
        }
        this._platformAPIService.setAppId(fullAppId);
        this._platformAPIService.resumeProgress();

        const saveGame = this._saveModel.getSaveGame();
        // NB: if save game is completed, do not load it again
        if (!!saveGame && !saveGame.completed) {
            this._gameModel.elapsedTime = saveGame.elapsedTime;
            this._storyMemento = saveGame.storyMemento;
            this._dialogMemento = saveGame.dialogMemento;
            this._storyVariables = saveGame.storyVariables;

            this._trackingController.trackProgress(ProgressValue.RESUMED, saveGame.elapsedTime);
        }

        // Load scenario assets
        this._dispatcher.addEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoadedBound);
        this._assetLoadService.loadGroups(this._scenarioToPlay.id);
        //also add to assetLocator (so DialogEditorGateway can find assets)
        this._assetLocator.addManifest(this._assetLoadService.getManifest(this._scenarioToPlay.id));

        // update elapsed time
        createjs.Ticker.addEventListener("tick", this._handleTickBound);
    }

    onScenarioLoaded(event) {
        this._dispatcher.removeEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoadedBound);

        // Set the current scenario in the library
        this._scenarioLibrary.setCurrentScenario(this._scenarioToPlay);

        // Start a new scenario play session
        this._playSessionModel.startNewScenarioSession(this._scenarioToPlay.id);

        // Create story
        this._scenarioToPlay.story = this._storyFactory.getStory(this._scenarioToPlay.storyID);

        // Initialize scenario 
        // From story memento, if any; else start with clear variable manager
        this._variableManager.reset();
        if (this._storyVariables) {
            this._variableManager.setState(this._storyVariables);
        }
        this._scenarioPlayer.init(this._scenarioToPlay, this._storyMemento);

        this._trackingController.trackProgress(ProgressValue.STARTED);

        var sprEvent = new ScenarioPlayerRegisterEvent(ScenarioPlayerRegisterEvent.REGISTER, this._scenarioPlayer);
        this._dispatcher.dispatch(sprEvent.type, sprEvent);

        // load dialog screen
        const isRestoredSaveGame = !!this._storyMemento && !!this._dialogMemento;
        this._dialogScreenSkin = this.gotoScreen(SkinKeys.DIALOG_SCREEN_SKIN, {
            isRestoredSaveGame: isRestoredSaveGame,
        });

        // Start the scenario!
        this._startTime = Date.now();
        this._scenarioPlayer.eventDispatcher.addEventListener(ScenarioPlayerEvent.SCENARIO_END, this._onScenarioEndBound);
        this._scenarioPlayer.play();

        if (!isRestoredSaveGame) {
            this.dispatch(SaveEvent.SAVE);
        }

        if (isRestoredSaveGame) {
            const contentLabel = this._dialogMemento.contentLabel;
            const storyNode = this._scenarioPlayer.getStoryNodeByContentLabel(contentLabel);
            const content = storyNode.getData();
            //Dispatch a ScenarioPlayerEvent.PUSH_DIALOG_AVAILABLE with an extra parameter: dialogMemento
            //NB: we dont check whether this is a push or pull dialog, because this project only uses push!
            this.dispatch(ScenarioPlayerEvent.PUSH_DIALOG_AVAILABLE, {
                data: {
                    content: content,
                    contentPlayer: this._scenarioPlayer,
                    dialogMemento: this._dialogMemento,
                }
            });
        }
    }

    onScenarioEnd(event) {
        this._scenarioPlayer.eventDispatcher.removeEventListener(ScenarioPlayerEvent.SCENARIO_END, this._onScenarioEndBound);

        //wait 1 frame before tracking progress, to avoid dialog completion to be sent later then overall completion (due to order of eventlisteners)
        setTimeout(() => { this._trackingController.trackProgress(ProgressValue.COMPLETED); });

        var sprEvent = new ScenarioPlayerRegisterEvent(ScenarioPlayerRegisterEvent.DEREGISTER, this._scenarioPlayer);
        this._dispatcher.dispatch(sprEvent.type, sprEvent);

        this._dispatcher.removeEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoadedBound);
        this._scenarioPlayer.eventDispatcher.removeEventListener(ScenarioPlayerEvent.SCENARIO_END, this._onScenarioEndBound);
        this._scenarioPlayer.stop();

        createjs.Ticker.removeEventListener("tick", this._handleTickBound);

        this.dispatch(ScenarioCommandEvent.FINISH_SCENARIO, {
            scenario: this._scenarioToPlay,
        });
    }

    _handleTick(e) {
        this._gameModel.elapsedTime += e.delta;
    }
}

